import { ArrayUtil, AuthType, Collection, DisplayValue, DynamicLoader, getLogger, getThemeColor, HorizontalAlignment, makeStyles, ModelRow, ServerError, StringUtil } from "@mcleod/core";
import { Component } from "../../base/Component";
import { ReflectiveDialogs } from "../../base/ReflectiveDialogs";
import { deserializeComponents } from "../../serializer/ComponentDeserializer";
import { Panel } from "../panel/Panel";
import { Label } from "../label/Label";
import { Button, DataHeader, DataSource, DataSourceExecutionEvent, DataSourceMode, PropType, ScreenStack } from "../..";
import { LayoutPropDefinitions, LayoutProps } from "./LayoutProps";
import { ListenerListDef } from "../../base/ListenerListDef";
import { Event, EventListener } from "../../events/Event";
import { ModelLayout } from "../../models/ModelLayout";
import { createDataSourcesFromDef } from "../../databinding/DataSource";
import { ComponentPropDefinition } from "../../base/ComponentProps";
import { ComponentTypes } from "../../base/ComponentTypes";
import { TransitionOptions } from "../../base/TransitionOptions";
import { Decorator } from "../../page/decorators/Decorator";
import { Spinner } from "../spinner/Spinner";
import { getAuthSettings, getUserAuthType } from "@mcleod/core/src/Settings";

const cachedLayouts: Collection<string> = {};
const log = getLogger("components/LayoutPanel");

const _loadListenerDef: ListenerListDef = { listName: "_loadListeners" };
const _loadErrorDef: ListenerListDef = { listName: "_errorListeners" };
const _dataLoadListenerDef: ListenerListDef = { listName: "_dataLoadListeners" };

const classes = makeStyles("lyt", () => {
  return {
    nestedDesignerOverlay: { width: "100%", height: "100%", position: "absolute", zIndex: 25, display: "flex",
      justifyContent: "center", flexDirection: "row", alignItems: "center",
      backgroundColor: "#5555000B", color: getThemeColor("primary") + "28"
    }
  }
});

/**
 * A Layout is a Panel subclass that gets its basic structure from a server-defined API call.  The server reads a .layout
 * file (in JSON format) and returns it to this class.  This class parses the result and adds the components defined in
 * the .layout.
 *
 * This .layout file is almost always created by the UI Designer page.
 *
 */
export class Layout extends Panel implements LayoutProps {
  private _layoutName: string;
  private _loaded: boolean;
  private _loadError: string;
  private _dataLoaded: boolean;
  private _dataLoadDataSources: DataSource[];
  public auth: string;
  public allowDecoratorOverride: boolean = true;
  public defaultDecorator: string;
  private _mainDataSource: DataSource;
  public needsServerLayout: boolean = true;
  private _title: string;
  private _titleImage: string;
  private _titleAddModeSuffix: string;
  private _titleEditModeSuffix: string;
  private _titleSearchModeSuffix: string;
  private _hideTitle: boolean;
  private _hideSuffix: boolean;
  public applyFieldLevelPermissions: boolean = true;
  public applyFieldLevelLicensing: boolean = true;
  public dataSourceIDs: string[];
  private _isNested: boolean;
  private _inheritParentDataSource:string;
  private _loadPromise: Promise<any>;
  private _titleSinglular: string;
  private _titlePlural: string;
  private displayedLoadError;
  private _dataHeader: DataHeader;
  private _helpLinks: string[];
  private _displayingFromToolbox: boolean;
  private loadLayoutWithoutDefError: boolean;

  constructor(props?: Partial<LayoutProps>, callSetProps: boolean = true) {
    super({
      fillRow: true,
      fillHeight: true,
      padding: 0,
      applyFieldLevelPermissions: props?.applyFieldLevelPermissions,
      applyFieldLevelLicensing: props?.applyFieldLevelLicensing,
      scrollX: LayoutPropDefinitions.getDefinitions().scrollX.defaultValue,
      scrollY: LayoutPropDefinitions.getDefinitions().scrollY.defaultValue,
      ...props
    }, callSetProps);

    if (props?.needsServerLayout === false) {
      this.ensureLoaded().then(() => {
          if (this.loadLayoutWithoutDefError !== true)
              this.loaded();
      });
  }
  }

  set layoutName(value: string) {
    if (this.layoutName === value)
      return;
    this._layoutName = value;
    this.loadLayout();
  }

  get layoutName(): string {
    return this._layoutName;
  }

  setProps(props: Partial<LayoutProps>) {
    if(props?.needsServerLayout != null) {
        this.needsServerLayout = props.needsServerLayout;
        delete props.needsServerLayout;
    }
    super.setProps(props);
}

  private loadLayout() {
    if (this.needsServerLayout === false) {
      this._loadPromise = this.loadLayoutWithoutDef().catch(error => {
        this.handleLoadError(error);
        this.loadLayoutWithoutDefError = true;
      });
      return;
    }
    const fieldLevelLicensingProp = {};
    if (this.applyFieldLevelLicensing === false)
      fieldLevelLicensingProp["apply_field_level_licensing"] = false;
    if (this.applyFieldLevelPermissions === false) {
      new ModelLayout().searchSingle({ path: this.layoutName, apply_field_level_perms: false, ...fieldLevelLicensingProp }).then(rowLayout => {
        this.displayLayout(rowLayout.get("response"));
      }).catch((error) => {
        log.error(error);
      });
    } else {
      const def = cachedLayouts[this.layoutName];
      if (def == null) {
        this.add(new Spinner());
        this._loadPromise = new ModelLayout().searchSingle({ path: this.layoutName, ...fieldLevelLicensingProp }).then(async (rowLayout) => {
          cachedLayouts[this.layoutName] = rowLayout.get("response");
          return await this.displayLayout(rowLayout.get("response"));
        }).catch((error) => {
          if (this.isNested && this._designer != null)
            this.removeAll();
          else {
            this.handleLoadError(error);
          }
        });
      }
      else
      this._loadPromise = this.displayLayout(def);
    }
  }

  private handleLoadError(error: any) {
    this.removeAll();
    log.info(error);
    this.displayLoadErrorPanel(error);
    this.fireListeners(_loadErrorDef, new Event(this));
  }

  private async loadLayoutWithoutDef() {
    if (!this.validateAuthType()) {
      throw Error("User is not authorized to view this layout")
    }

    if (getAuthSettings().permissions?.denied?.[this.layoutName]?.["Entire screen"] != null) {
      throw Error("Permission denied for this layout");
    }
  }

  private validateAuthType(): boolean {
    const authType = this.auth ?? AuthType.LME;
    return authType === AuthType.UNAUTH || authType === AuthType.ANY || authType === getUserAuthType();
  }

  private displayLoadErrorPanel(error) {
    const cls = DynamicLoader.getClassForPath("components/page/PanelStaticError");
    if (cls != null) {
      let errorSummary = "Sorry, there was an error loading this page.";
      if (error instanceof ServerError && error.errorTag == null && error.messages?.length == 1)
        errorSummary = error.messages[0];
      const inst = new cls({ errorSummary })
      this.add(inst);
    }
  }


  private async loaded() {
    if (!this._loaded) {
      if (this.isNested && this.inheritParentDataSource != null)
        this.replaceMainDatasource(this.owner?.[this.inheritParentDataSource]);
      this._loaded = true;
      await this.onLoad();
      this.fireListeners(_loadListenerDef, new Event(this));
      if (this.isNested != true && this.parent != null)
        ScreenStack.fireCurrentLayoutListeners();
    }
  }

  /**
   * This method is meant to be overridden by subclasses that might need to do something once all the components
   * are added to the screen.
   */
  onLoad(): void | Promise<void> {
  }

  addLayoutLoadListener(value: EventListener) {
    value.runsOnce = true;
    this.addEventListener(_loadListenerDef, value);
    if (this._loaded === true)
      this.fireListeners(_loadListenerDef, new Event(this));
  }

  removeLayoutLoadListener(value: EventListener) {
    this.removeEventListener(_loadListenerDef, value);
  }

  addLoadErrorListener(value: EventListener) {
    this.addEventListener(_loadErrorDef, value);
    if (this._loadError != null)
      this.fireListeners(_loadErrorDef, new Event(this));
  }

  removeLoadErrorListener(value: EventListener) {
    this.removeEventListener(_loadErrorDef, value);
  }

  /**
   * A Layout's data load listeners are meant to fire after the layout has been loaded, AND
   * all DataSources within the layout (including nested layouts) have finished searching.
   * This is done by:
   *  -> Gathering all DataSources (including those from nested layouts)
   *  -> Determining which of those DataSources are top-level DataSources
   *  -> See if any top-level DataSources are currently searching.
   *     Each DataSource still searching has an after execute listener added to it so it can notify us when it's done.
   *  -> DataSources that are not at the top-level (those that are a child of another DataSource) are handled by the DataSource class
   *     (since we use the afterSearchPlusChildren listener)
   *  -> DataSources that begin to search after this point are added to the list of DataSources and get a similar listener.
   *     This is the purpose of the DataSource's trackingLayout; it lets the DataSource be tied to these functions in the Layout.
   *  -> When no DataSources are still executing (when the array is empty), fire the data load listeners.
   *
   * Note that data load listeners added after the above process has taken place will run immediately.
   *
   * @param value the method to call when both the layout and the layout's data has been loaded
   */
   addLayoutDataLoadListener(value: EventListener) {
    value.runsOnce = true;
    const isFirstListener = !this.hasListeners(_dataLoadListenerDef) && this._dataLoaded !== true;
    this.addEventListener(_dataLoadListenerDef, value);
    if (this._loaded === true && this._dataLoaded === true) {
      this.fireListeners(_dataLoadListenerDef, new Event(this));
      return;
    }
    if (isFirstListener === true) {
      const topLevelDataLoadDataSources = DataSource.getTopLevelDataSources(this._dataLoadDataSources);
      this._dataLoadDataSources = null;
      if (ArrayUtil.isEmptyArray(topLevelDataLoadDataSources) === true) {
        this._dataLoaded = true;
        this._fireDataLoadedListeners();
        return;
      }
      for (const dataSource of topLevelDataLoadDataSources) {
        dataSource.trackingLayout = this;
        if (dataSource.isSearchingOrChildrenSearching === true)
          this.addDataLoadDataSource(dataSource);
      }
    }
  }

  removeLayoutDataLoadListener(value: EventListener) {
    this.removeEventListener(_dataLoadListenerDef, value);
  }

  addDataLoadDataSource(dataSource: DataSource) {
    if (this._dataLoaded === true) {
      dataSource.trackingLayout = null;
      return;
    }
    if (this._dataLoadDataSources == null)
      this._dataLoadDataSources = [];
    if (ArrayUtil.addNoDuplicates(this._dataLoadDataSources, dataSource) === true)
      this._addDataLoadDataSourceListener(dataSource);
  }

  private _addDataLoadDataSourceListener(dataSource: DataSource) {
    const callback: EventListener = (event: DataSourceExecutionEvent) => this._removeDataLoadDataSource(this, event.dataSource);
    callback.runsOnce = true;
    dataSource.addAfterSearchPlusChildrenListener(callback);
  }

  private _removeDataLoadDataSource(layout: Layout, dataSource: DataSource) {
    ArrayUtil.removeFromArray(layout._dataLoadDataSources, dataSource);
    if (layout._dataLoaded === true)
      return;
    if (ArrayUtil.isEmptyArray(layout._dataLoadDataSources) === true) {
      layout._dataLoaded = true;
      this._fireDataLoadedListeners(layout);
    }
  }

  private _fireDataLoadedListeners(layout: Layout = this) {
    layout.addLayoutLoadListener(event => layout.fireListeners(_dataLoadListenerDef, new Event(layout)));
  }

  override getPropertyDefinitions() {
    if (this.isNested)
      return {...LayoutPropDefinitions.getDefinitions(), id: {
        type: PropType.string,
        description: "The identifier for this nested layout."
      }
    }
    return LayoutPropDefinitions.getDefinitions();
  }

  search(filter: any): Promise<any> {
    if (this.mainDataSource == null)
      throw new Error("Can't search a layout without a mainDataSource.");
    return this.mainDataSource.search(filter);
  }

  ensureLoaded(): Promise<any> {
    if (this._loadPromise != null)
      return this._loadPromise;
    else
      return Promise.resolve(null);
  }

  async displayLayout(defStr: string) {
    try {
      this.removeAll();
      const def = JSON.parse(defStr);
      let dataSources: Collection<DataSource>;
      const dataSourceIDs = [];
      if (def.dataSources != null) {
        const sources = createDataSourcesFromDef(def.dataSources, this, this.layoutName);
        dataSources = {};
        for (const dataSource of sources) {
          dataSources[dataSource.id] = dataSource;
          dataSourceIDs.push(dataSource.id);
          this[dataSource.id] = dataSource;
          await dataSource.getMetadata();
        }
      }
      for (const key in LayoutPropDefinitions.getDefinitions()) {
        let value = def[key];
        if (value != null) {
          if (key === "mainDataSource")
            value = dataSources[value];
          this[key] = value;
        }
      }
      const components = deserializeComponents(this, def.components, this._designer, null, dataSources,
        (type: string, props: any) => this.createComponentForDeserialization(type, props));
      deserializeComponents(this, def.permed, this._designer, null, dataSources,
          (type: string, props: any) => this.createComponentForDeserialization(type, props));
      for (const comp of components) {
        if (!(comp instanceof DataHeader))
          this.add(comp);
        else
          this.dataHeader = comp;
      }

      for (const dataSourceID of dataSourceIDs) {
        const dataSource = dataSources[dataSourceID];
        if (dataSource.autoSearch === true) {
          dataSource.mode = DataSourceMode.SEARCH;
        }
      }
      this.dataSourceIDs = [...dataSourceIDs];
      this._dataLoadDataSources = this._getDataSources();
      await this.loadComponentMetadata();
      for (const comp of this.getRecursiveChildren()) {
        if (comp instanceof Layout) {
          await comp.ensureLoaded();
          this._dataLoadDataSources.push(...comp._getDataSources());
        }
      }
      await this.loaded();
    }
    catch (err) {
      log.error("Error loading page", err);
      if (this.displayedLoadError !== true) {
        this.displayedLoadError = true;
        ReflectiveDialogs.showError(err);
      }
    }
  }

  private _getDataSources(): DataSource[] {
    const result: DataSource[] = [];
    for (const dataSourceID of this.dataSourceIDs)
      result.push(this[dataSourceID] as DataSource);
    return result;
  }

  private async loadComponentMetadata() {
    for (const comp of this.getRecursiveChildren())
      await comp.loadMetadata();
  }

  createComponentForDeserialization(type: string, props: any): Component {
    return ComponentTypes.createComponentOfType(type, props);
  }

  get title(): string {
    const overriddenTitle = this.overrideTitle();
    if (overriddenTitle != null)
      return overriddenTitle;
    return this.getTitleForMode(this.mainDataSource?.mode);
  }

  set title(value: string) {
    this._title = value;
  }

  public getTitleForMode(mode: DataSourceMode): string {
    if (mode == null)
      mode = DataSourceMode.NONE;
    const row = this.mainDataSource?.activeRow;
    let result = DisplayValue.getFormattedDataString(this._title, row);
    let suffix = null;
    switch (mode) {
      case DataSourceMode.UPDATE: suffix = DisplayValue.getFormattedDataString(this.titleEditModeSuffix, row); break;
      case DataSourceMode.ADD: suffix = DisplayValue.getFormattedDataString(this.titleAddModeSuffix, row); break;
      case DataSourceMode.SEARCH: suffix = DisplayValue.getFormattedDataString(this.titleSearchModeSuffix, row); break;
    }
    if (!this._hideSuffix && !StringUtil.isEmptyString(suffix))
      result += " - " + suffix;
    return result;
  }

  get titleImage(): string {
    return this._titleImage;
  }

  set titleImage(value: string) {
    this._titleImage = value;
  }

  overrideTitle(): string {
    return null;
  }

  get titleAddModeSuffix(): string {
    return this._titleAddModeSuffix || this.getDefaultTitleAddModeSuffix();
  }

  set titleAddModeSuffix(value: string) {
    this._titleAddModeSuffix = value;
  }

  private getDefaultTitleAddModeSuffix() {
    return "New " + (this.titleSingular || "record");
  }

  get titleEditModeSuffix(): string {
    return this._titleEditModeSuffix || this.getDefaultTitleEditModeSuffix();
  }

  set titleEditModeSuffix(value: string) {
    this._titleEditModeSuffix = value;
  }

  private getDefaultTitleEditModeSuffix(): string {
    let result = "Edit " + (this.titleSingular || "record");
    if (this.mainDataSource instanceof DataSource) {
      let displayFormat = this.mainDataSource?.getMetadataFromCache()?.displayField;
      if (displayFormat != null && displayFormat.indexOf("{") < 0)
        displayFormat = "{" + displayFormat + "}";
      if (displayFormat != null)
        result += " - " + displayFormat;
    }
    return result;
  }

  get titleSearchModeSuffix() {
    return this._titleSearchModeSuffix || this.getDefaultTitleSearchModeSuffix();
  }

  set titleSearchModeSuffix(value: string) {
    this._titleSearchModeSuffix = value;
  }

  private getDefaultTitleSearchModeSuffix(): string {
    return "Advanced Search";
  }

  get hideTitle(): boolean {
    return this._hideTitle;
  }

  set hideTitle(value: boolean) {
    this._hideTitle = value;
  }

  get hideSuffix(): boolean {
    return this._hideSuffix;
  }

  set hideSuffix(value: boolean) {
    this._hideSuffix = value;
  }

  override get serializationName() {
    return "layout";
  }

  public setAllDataSourcesToMode(mode:DataSourceMode):void {
    this.dataSourceIDs.forEach( (id) =>{
      if ((this.mainDataSource == null) || (id !== this.mainDataSource.id))
        (this[id] as DataSource).mode=mode;
    });
    if( this.mainDataSource != null)
      this.mainDataSource.mode=mode;
  }

  public clearNonMainDataSources() {
    for (const dataSourceID of this.dataSourceIDs) {
      if (dataSourceID === this.mainDataSource.id)
        continue;
      const dataSource = this[dataSourceID] as DataSource;
      dataSource.clear();
    }
  }

  public get titleSingular() {
    return this._titleSinglular || this.getDefaultTitleSingular();
  }

  public set titleSingular(value: string) {
    this._titleSinglular = value;
  }

  private getDefaultTitleSingular(): string {
    if (this._titlePlural != null || this._title != null) {
      const plural = this._titlePlural || this._title;
      if (plural.endsWith("es"))
        return plural.substring(0, plural.length - 2);
      if (plural.endsWith("s"))
        return plural.substring(0, plural.length - 1);
    }
    return undefined;
  }

  public get titlePlural() {
    return this._titlePlural || this.getDefaultTitlePlural();
  }

  public set titlePlural(value: string) {
    this._titlePlural = value;
  }

  private getDefaultTitlePlural(): string {
    if (this._title != null)
      return this._title;
    if (this._titleSinglular != null)
      return this._titleSinglular + "s";
    return undefined;
  }

  _serializeNonProps():string {
    if (this.components != null && this.components.length > 0 && this.isNested !== true)
      return super._serializeNonProps();
    return "";
  }

  public get isNested(): boolean {
    return this._isNested;
  }

  public set isNested(value: boolean) {
    this._isNested = value;
  }

  layoutRows() {
    super.layoutRows();
    if (this.isNested && this._designer != null && !this.owner?.isNested)
      this._element.appendChild(this.createNestedDesignerOverlay());
  }

  private createNestedDesignerOverlay() {
    const result = new Panel({ id: "nested-designer-overlay", className: classes.nestedDesignerOverlay });
    const button = new Button({ caption: "Nested Layout ", borderWidth: 0, imageName: "detail", tooltip: "Open " + this.layoutName +  " in another designer tab", imageHeight: 32, imageWidth: 32, align: HorizontalAlignment.CENTER, fontSize: 40 });
    if (this._designer?.openTab != null)
      button.addClickListener(() => this._designer.openTab(this.layoutName, true, true));
    result.add(button);
    return result._element;
  }

  public get inheritParentDataSource(): string {
    return this._inheritParentDataSource;
  }

  public set inheritParentDataSource(value: string) {
    this._inheritParentDataSource = value;
  }

  override getListenerDefs(): Collection<ListenerListDef> {
    return {
      ...super.getListenerDefs(),
      "loadListener": { ..._loadListenerDef },
      "loadError": { ..._loadErrorDef }
    };
  }

  public get mainDataSource(): DataSource {
    return this._mainDataSource;
  }

  public set mainDataSource(value: DataSource) {
    this._mainDataSource = value;
  }

  override getPropertyDefaultValue(prop: ComponentPropDefinition) {
      if (prop.name === "titleSingular")
        return this.getDefaultTitleSingular();
      if (prop.name === "titlePlural")
        return this.getDefaultTitlePlural();
      if (prop.name === "titleAddModeSuffix")
        return this.getDefaultTitleAddModeSuffix();
      if (prop.name === "titleEditModeSuffix")
        return this.getDefaultTitleEditModeSuffix();
      if (prop.name === "titleSearchModeSuffix")
        return this.getDefaultTitleSearchModeSuffix();
      return super.getPropertyDefaultValue(prop);
  }

  override _deserializeSpecialProps(componentOwner, compDef, defaultPropValues, dataSources, callback): string[] {
    if (compDef.mainDataSource != null && dataSources != null)
      this.mainDataSource = dataSources[compDef.mainDataSource];
    return ["mainDataSource", ...super._deserializeSpecialProps(componentOwner, compDef, defaultPropValues, dataSources, callback)];
  }

  public replaceMainDatasource(newDatasource:DataSource) {
    if (newDatasource != null) {
      this.mainDataSource.rebindComponentsTo(newDatasource);
      newDatasource.attachListeners(this.mainDataSource);
      for (const dataSourceID of this[this.mainDataSource.id].getChildDataSourceIds())
        this[dataSourceID].parentDataSource = newDatasource;

      this.mainDataSource = newDatasource;
      this[this.mainDataSource.id] = newDatasource;
    }
  }

  public static clearCachedLayout(name: string): void {
    delete cachedLayouts[name];
  }

  public static getLayout(layoutName: string, props?: Partial<LayoutProps>): Layout {
    const cls = DynamicLoader.getClassForPath(layoutName);
    if (cls == null)
      return new Layout({ layoutName: layoutName, ...props })
    else
      return new cls({ layoutName: layoutName, ...props });
  }

  get dataHeader(): DataHeader {
    return this._dataHeader;
  }

  set dataHeader(value: DataHeader) {
    this._dataHeader = value;
  }

  public getDataHeaderAddlLeftComponents(): Component[] {
    return null;
  }

  public getDataHeaderAddlRightComponents(): Component[] {
    return null;
  }

  public getDataHeaderEllipsisActions(): Label[] {
    return null;
  }

  /**
   * Override so that a layout can first check to see if it's a child of a Decorator...if it is, slide that decorator out instead
   * @param options
   * @param componentToFocusOnClose
   * @returns
   */
  public override slideOut(options?: TransitionOptions, componentToFocusOnClose?: Component): Promise<any> {
    const decorator = this._getParentDecorator();
    if (decorator == null)
      return super.slideOut(options, componentToFocusOnClose);
    else
      return decorator.slideOut(options, componentToFocusOnClose);
  }

  private _getParentDecorator(): Decorator {
    let parent = this.parent;
    while (parent != null) {
      if (parent instanceof Decorator)
        return parent;
      parent = parent.parent;
    }
    return null;
  }

  public get activeRow(): ModelRow<any> {
    return this.mainDataSource?.activeRow;
  }

  public get helpLinks(): string[] {
    return this._helpLinks;
  }

  public set helpLinks(value: string[]) {
    this._helpLinks = value;
  }
  public get displayingFromToolbox(): boolean {
    return this._displayingFromToolbox == null ? false : this._displayingFromToolbox;
  }

  public set displayingFromToolbox(value: boolean) {
    this._displayingFromToolbox = value;
  }
}


ComponentTypes.registerComponentType("layout", Layout.prototype.constructor, false, [ "layoutName" ]);
